import { api } from '@goaltree/backend/convex/_generated/api'
import { realisticCoach } from '@goaltree/backend/convex/lib/coach'
import { Dialog, DialogContent } from '@goaltree/ui/components/dialog'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CoachIntake } from '@/components/coach-intake'
import { LoginForm } from '@/components/login-form'
import { useSignedIn } from '@/hooks/use-signed-in'
import { useTurnstile } from '@/hooks/use-turnstile'
import { intakeAnonId } from '@/lib/intake-anon'
import { posthog } from '@/lib/posthog'
import { stashReturnTo } from '@/lib/return-to'

const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

// "Talk to Caesar · 90 sec" from the homepage (and Set me up on the signed-out
// onboarding step): the intake with no account. Sign-up only comes at "See
// Caesar live" or "Save my plan", and the answers carry over.
export const Route = createFileRoute('/try')({
  head: () => ({
    meta: [
      { title: 'Talk to your coach — Goaltree' },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  component: TryRoute,
})

const claimPath = (live: boolean) => `/onboarding/intake?claim=true${live ? '&live=true' : ''}`

function TryRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const signedIn = useSignedIn()
  const [anonId] = useState(() => intakeAnonId(true))
  const [signUp, setSignUp] = useState<'live' | 'save' | null>(null)
  const pendingRef = useRef<string | null>(null)
  const turnstile = useTurnstile()
  const landed = useRef(false)
  const visitor = useQuery(api.intakeAnon.get, anonId ? { anonId } : 'skip')
  const coach = realisticCoach(visitor?.coachVoice ?? 'face:caesar')?.name ?? 'Caesar'

  useEffect(() => {
    if (landed.current) return
    landed.current = true
    const qs = new URLSearchParams(location.search)
    posthog.capture('try_landing', {
      ...Object.fromEntries(UTM.flatMap((k) => (qs.get(k) ? [[k, qs.get(k)]] : []))),
      lang: qs.get('lang') ?? undefined,
    })
  }, [])

  // Signed in (already, or just now through the email code): carry on as them.
  useEffect(() => {
    if (signedIn !== 'in') return
    void navigate({ href: pendingRef.current ?? claimPath(false), replace: true })
  }, [signedIn, navigate])

  function openSignUp(where: 'live' | 'save') {
    posthog.capture('intake_signup_prompt', { where, anonymous: true })
    pendingRef.current = claimPath(where === 'live')
    stashReturnTo(pendingRef.current)
    setSignUp(where)
  }

  // The Turnstile widget mounts once, so its box exists before we know who's here.
  const widget = (
    <div ref={turnstile.containerRef} className="fixed bottom-2 left-1/2 z-50 -translate-x-1/2" />
  )
  if (!anonId || signedIn !== 'out') return <>{widget}</>

  return (
    <>
      {widget}
      <CoachIntake
        anon={{
          id: anonId,
          onSignUp: openSignUp,
          turnstileToken: async () => {
            const t = await turnstile.waitForToken(15000)
            turnstile.reset()
            return t
          },
        }}
        onRequestLive={() => {
          openSignUp('live')
          return false
        }}
        onBack={() => (router.history.length > 1 ? router.history.back() : navigate({ to: '/' }))}
      />
      <Dialog open={!!signUp} onOpenChange={(open) => !open && setSignUp(null)}>
        <DialogContent>
          <LoginForm
            returnTo={pendingRef.current ?? claimPath(false)}
            surface="modal"
            title={
              signUp === 'live'
                ? t('intake.signupLiveTitle', { name: coach })
                : t('intake.signupSaveTitle')
            }
            subtitle={t('intake.signupHint')}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
